class TDLoadMorePagination extends HTMLElement {
  constructor() {
    super();
    this.CONTAINER_SELECTOR = ".product-grid-container";
    this.WRAPPER_SELECTOR = ".product-list-wrapper";
    this.ITEM_SELECTOR = ".grid__item";
    this.GRID_SELECTOR = "#product-grid";
    this.TEXT_SELECTOR = ".td-page__text";
    this.LOADER_SELECTOR = ".td-page__loader-wrapper";
    this.RESULT_IMAGE_SELECTOR = ".card__media img";

    this.LOADER_HIDDEN_CLASS = "td-page__loader--inactive";
    this.TEXT_HIDDEN_CLASS = "td-page__text--inactive";
    this.ITEM_LOAD_IN_CLASS = "td--item--load_in";
    this.NEXT_URL_DATASET_KEY = "nextUrl";

    this.ANIMATION_DELAY = 100;
    this.LOADER_SHOW_DELAY_MS = 100;
    this.LOADER_HIDE_DELAY_MS = 1000;
    this.ITEMS_TO_EAGER_LOAD = 2;
    this.MAX_EMPTY_PAGES_TO_SKIP = 10;
    this.FETCH_TIMEOUT = 10000;

    this.isLoading = false;
    this.container = null;
    this.sectionId = "";
    this.loader = null;
    this.textWrapper = null;
    this._boundHandleLoadMore = null;
    this._boundHandleKeydown = null;
    this._timeoutIds = [];
    this._isDisconnected = false;
    this._pendingFetchControllers = new Set();
  }

  connectedCallback() {
    this._isDisconnected = false;
    this.container = this.closest(this.CONTAINER_SELECTOR);
    this.sectionId = this.getSectionId(this.container);
    this.loader = this.querySelector(this.LOADER_SELECTOR);
    this.textWrapper = this.querySelector(this.TEXT_SELECTOR);
    if (!this.hasAttribute("role")) this.setAttribute("role", "button");
    if (!this.hasAttribute("tabindex")) this.setAttribute("tabindex", "0");

    if (!this.getNextUrl()) {
      this.hideControl();
      return;
    }

    this.attachListeners();
    this.updateAccessibilityState();
  }

  disconnectedCallback() {
    this._isDisconnected = true;
    this.isLoading = false;
    this.abortPendingFetches();

    if (this._boundHandleLoadMore) {
      this.removeEventListener("click", this._boundHandleLoadMore);
      this._boundHandleLoadMore = null;
    }

    if (this._boundHandleKeydown) {
      this.removeEventListener("keydown", this._boundHandleKeydown);
      this._boundHandleKeydown = null;
    }

    this.clearScheduledTimeouts();
  }

  attachListeners() {
    if (!this._boundHandleLoadMore) {
      this._boundHandleLoadMore = this.handleLoadMore.bind(this);
    }

    if (!this._boundHandleKeydown) {
      this._boundHandleKeydown = this.handleKeydown.bind(this);
    }

    this.addEventListener("click", this._boundHandleLoadMore);
    this.addEventListener("keydown", this._boundHandleKeydown);
  }

  scheduleTimeout(callback, delay) {
    const timeoutId = setTimeout(() => {
      this._timeoutIds = this._timeoutIds.filter((id) => id !== timeoutId);
      if (this.isComponentDisconnected()) return;
      callback();
    }, delay);
    this._timeoutIds.push(timeoutId);
    return timeoutId;
  }

  clearScheduledTimeouts() {
    this._timeoutIds.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this._timeoutIds = [];
  }

  abortPendingFetches() {
    this._pendingFetchControllers.forEach((controller) => {
      controller.abort();
    });
    this._pendingFetchControllers.clear();
  }

  isAbortError(error) {
    return error?.name === "AbortError" || error?.code === 20;
  }

  isComponentDisconnected() {
    return this._isDisconnected || !this.isConnected;
  }

  getWrapper() {
    if (!this.container) return null;
    return this.container.querySelector(this.WRAPPER_SELECTOR);
  }

  getSectionId(root) {
    if (!root) return "";
    const grid = root.querySelector(this.GRID_SELECTOR);
    return grid && grid.dataset ? grid.dataset.id || "" : "";
  }

  getMatchedContainer(root) {
    if (!root) return null;
    const containers = Array.from(root.querySelectorAll(this.CONTAINER_SELECTOR));
    if (!containers.length) return null;
    if (!this.sectionId) return containers[0];

    return containers.find((container) => this.getSectionId(container) === this.sectionId) || null;
  }

  getNextUrl() {
    return this.dataset[this.NEXT_URL_DATASET_KEY] || "";
  }

  setNextUrl(url) {
    if (url) {
      this.dataset[this.NEXT_URL_DATASET_KEY] = url;
    } else {
      delete this.dataset[this.NEXT_URL_DATASET_KEY];
    }
    this.updateAccessibilityState();
  }

  handleKeydown(event) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    this.handleLoadMore(event);
  }

  isLoaderActive() {
    return Boolean(this.loader) && !this.loader.classList.contains(this.LOADER_HIDDEN_CLASS);
  }

  updateAccessibilityState() {
    if (this.isComponentDisconnected()) return;

    const isHidden = this.style.display === "none";
    const hasNextUrl = Boolean(this.getNextUrl());
    const isBusy = this.isLoading || this.isLoaderActive();
    const isDisabled = isHidden || !hasNextUrl || isBusy;

    this.setAttribute("aria-busy", isBusy ? "true" : "false");
    this.setAttribute("aria-disabled", isDisabled ? "true" : "false");

    if (isHidden) {
      this.setAttribute("aria-hidden", "true");
      this.setAttribute("tabindex", "-1");
    } else {
      this.removeAttribute("aria-hidden");
      this.setAttribute("tabindex", "0");
    }
  }

  isActivationBlocked() {
    return this.getAttribute("aria-disabled") === "true" || this.getAttribute("aria-busy") === "true";
  }

  handleLoadMore(event) {
    if (event?.type === "click" && event.button !== 0) return;
    if (this.isActivationBlocked()) return;
    if (this.isComponentDisconnected()) return;

    const wrapper = this.getWrapper();
    const initialNextUrl = this.getNextUrl();
    if (!wrapper || !initialNextUrl) {
      this.hideControl();
      return;
    }

    this.isLoading = true;
    this.showLoading();
    this.updateAccessibilityState();

    this.appendNextAvailablePage(wrapper, initialNextUrl, 0)
      .catch((err) => {
        if (this.isAbortError(err) || this.isComponentDisconnected()) return;
        console.error("Something went wrong.", err);
      })
      .finally(() => {
        if (this.isComponentDisconnected()) return;
        this.finalizeLoading();
      });
  }

  appendNextAvailablePage(wrapper, nextUrl, pagesChecked) {
    if (this.isComponentDisconnected()) {
      return Promise.resolve();
    }

    if (!nextUrl || pagesChecked >= this.MAX_EMPTY_PAGES_TO_SKIP) {
      this.hideControl();
      return Promise.resolve();
    }

    return this.fetchHtml(nextUrl).then((html) => {
      if (this.isComponentDisconnected()) return;

      const { additionalResults, nextPageUrl } = this.parseResponse(html);
      if (this.isComponentDisconnected()) return;
      this.setNextUrl(nextPageUrl);

      if (additionalResults.length) {
        this.appendResults(wrapper, additionalResults);
        if (!nextPageUrl) this.hideControl();
        return;
      }

      if (!nextPageUrl) {
        this.hideControl();
        return;
      }

      return this.appendNextAvailablePage(wrapper, nextPageUrl, pagesChecked + 1);
    });
  }

  fetchHtml(url) {
    if (this.isComponentDisconnected()) {
      return Promise.reject(new DOMException("Load more request aborted", "AbortError"));
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.FETCH_TIMEOUT);
    this._pendingFetchControllers.add(controller);

    return fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Load more request failed (${response.status})`);
        }
        return response.text();
      })
      .catch((error) => {
        if (this.isAbortError(error)) {
          if (this.isComponentDisconnected()) {
            throw error;
          }
          throw new Error("Load more request failed (timeout)");
        }
        throw error;
      })
      .finally(() => {
        clearTimeout(timeoutId);
        this._pendingFetchControllers.delete(controller);
      });
  }

  parseResponse(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const matchedContainer = this.getMatchedContainer(doc);
    if (!matchedContainer) {
      return { additionalResults: [], nextPageUrl: "" };
    }

    const matchedWrapper = matchedContainer.querySelector(this.WRAPPER_SELECTOR);
    const additionalResults = matchedWrapper ? Array.from(matchedWrapper.querySelectorAll(this.ITEM_SELECTOR)) : [];
    const nextControl = matchedContainer.querySelector("td-load-more-pagination");
    const nextPageUrl = nextControl && nextControl.dataset ? nextControl.dataset[this.NEXT_URL_DATASET_KEY] || "" : "";

    return { additionalResults, nextPageUrl };
  }

  appendResults(wrapper, results) {
    if (this.isComponentDisconnected()) return;

    results.forEach((result, index) => {
      result.classList.add(this.ITEM_LOAD_IN_CLASS);
      if (index < this.ITEMS_TO_EAGER_LOAD) {
        const mainImage = result.querySelector(this.RESULT_IMAGE_SELECTOR);
        if (mainImage) mainImage.setAttribute("loading", "eager");
      }
      wrapper.appendChild(result);
      this.scheduleTimeout(() => {
        result.classList.remove(this.ITEM_LOAD_IN_CLASS);
      }, this.ANIMATION_DELAY * (index + 1));
    });
  }

  showLoading() {
    if (this.isComponentDisconnected()) return;
    if (!this.loader || !this.textWrapper) return;

    this.textWrapper.classList.add(this.TEXT_HIDDEN_CLASS);
    this.loader.style.display = "";
    this.updateAccessibilityState();
    this.scheduleTimeout(() => {
      this.loader.classList.remove(this.LOADER_HIDDEN_CLASS);
      this.updateAccessibilityState();
    }, this.LOADER_SHOW_DELAY_MS);
  }

  finalizeLoading() {
    if (this.isComponentDisconnected()) return;

    this.isLoading = false;
    this.updateAccessibilityState();
    if (this.loader && this.textWrapper) {
      this.scheduleTimeout(() => {
        this.loader.classList.add(this.LOADER_HIDDEN_CLASS);
        this.textWrapper.style.display = "";
        this.textWrapper.classList.remove(this.TEXT_HIDDEN_CLASS);
        this.updateAccessibilityState();
      }, this.LOADER_HIDE_DELAY_MS);
    }
    document.dispatchEvent(new Event("td-load-more-pagination-complete"));
  }

  hideControl() {
    if (this.isComponentDisconnected()) return;

    this.style.display = "none";
    this.updateAccessibilityState();
  }
}

function defineTDLoadMorePagination() {
  if (!customElements.get("td-load-more-pagination")) {
    customElements.define("td-load-more-pagination", TDLoadMorePagination);
  }
}

window.TDLoadMorePaginationSharedModule = Object.assign(window.TDLoadMorePaginationSharedModule || {}, {
  TDLoadMorePagination,
  defineTDLoadMorePagination,
});
